// The account settings' pure rules: the Mac's own-profile expiry label (a port of
// `OwnProfileView.formatExpiry`) plus the web-only settings the apps never edit but the Mattermost
// webapp did — Do Not Disturb durations, mention keywords, collapsed reply threads, name display,
// the clock, active sessions and the password form. Every rule here decides something a person
// sees or a value the server stores, so each is tested (accountSettings.test.ts).
import { civil, isSameDay } from './time';
import { addingDays, epochForCivil } from './zonedTime';

// MARK: - Expiry label (OwnProfileView.formatExpiry)

/**
 * "3:45 p.m." for today's and tomorrow's expiries, "dom 12:00 a.m." beyond — short and
 * unambiguous. Mirrors Android's `formatExpiry`. `now` is compared against, never the system
 * clock (the Apple comment: an injected clock the body ignores is a test that expires).
 */
export function formatExpiry(ms: number, now: number, locale: string, timeZone?: string, hour12?: boolean): string {
  const tomorrow = addingDays(now, 1, timeZone);
  const near = isSameDay(ms, now, timeZone) || isSameDay(ms, tomorrow, timeZone);
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit', timeZone };
  if (hour12 !== undefined) options.hour12 = hour12;
  if (!near) options.weekday = 'short';
  return new Intl.DateTimeFormat(locale, options).format(ms);
}

// MARK: - Do Not Disturb durations (webapp)

/** The webapp's "No molestar" submenu: until cleared, 30 min, 1 h, 2 h, tomorrow morning. */
export type DndDuration = 'dontClear' | 'thirtyMinutes' | 'oneHour' | 'twoHours' | 'tomorrow';
export const dndDurations: readonly DndDuration[] = ['dontClear', 'thirtyMinutes', 'oneHour', 'twoHours', 'tomorrow'];

/**
 * `dnd_end_time` for `PUT /users/{id}/status` — Unix SECONDS (the server's unit for this field,
 * unlike every other timestamp it sends), 0 = until cleared. "Tomorrow" is tomorrow 9:00 on the
 * reader's own clock, as in the webapp.
 */
export function dndEndTime(d: DndDuration, now: number, timeZone?: string): number {
  const seconds = (ms: number) => Math.floor(ms / 1000);
  switch (d) {
    case 'dontClear':
      return 0;
    case 'thirtyMinutes':
      return seconds(now + 30 * 60_000);
    case 'oneHour':
      return seconds(now + 60 * 60_000);
    case 'twoHours':
      return seconds(now + 2 * 60 * 60_000);
    case 'tomorrow': {
      const c = civil(now, timeZone);
      return seconds(epochForCivil(c.year, c.month, c.day + 1, 9, 0, 0, timeZone));
    }
  }
}

// MARK: - Mention keywords (notify_props.mention_keys)

/**
 * `mention_keys` is ONE comma-separated string that also carries the username entries the server
 * seeds (`alex`, `@alex`). The settings page edits only the person's OWN words; the username
 * entries ride along untouched, so saving a keyword never changes how @-mentions work.
 */
export function splitMentionKeys(raw: string, username: string): { custom: string[]; preserved: string[] } {
  const custom: string[] = [];
  const preserved: string[] = [];
  const own = username.toLowerCase();
  for (const piece of raw.split(',')) {
    const k = piece.trim();
    if (!k) continue;
    const bare = k.toLowerCase().replace(/^@/, '');
    if (own && bare === own) preserved.push(k);
    else custom.push(k);
  }
  return { custom, preserved };
}

/** Trims, drops blanks and commas, and dedupes case-insensitively, first spelling wins. */
export function normalizeKeywords(words: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of words) {
    for (const piece of w.split(',')) {
      const k = piece.trim();
      if (!k || seen.has(k.toLowerCase())) continue;
      seen.add(k.toLowerCase());
      out.push(k);
    }
  }
  return out;
}

export function joinMentionKeys(preserved: readonly string[], custom: readonly string[]): string {
  return normalizeKeywords([...preserved, ...custom]).join(',');
}

// MARK: - Display preferences (display_settings)

/**
 * Collapsed reply threads: the server's `CollapsedThreads` decides whether the reader may choose
 * at all. `default_on`/`default_off` let them (the preference `collapsed_reply_threads` = on/off
 * overrides the default); `always_on` and `disabled` do not; anything unknown reads as disabled.
 */
export function threadsChoice(config: string | null | undefined, preference: string | null | undefined): { editable: boolean; on: boolean } {
  switch (config) {
    case 'always_on':
      return { editable: false, on: true };
    case 'default_on':
      return { editable: true, on: preference ? preference === 'on' : true };
    case 'default_off':
      return { editable: true, on: preference === 'on' };
    default:
      return { editable: false, on: false };
  }
}

export type NameFormat = 'username' | 'nickname_full_name' | 'full_name';
export const nameFormats: readonly NameFormat[] = ['full_name', 'nickname_full_name', 'username'];

/** What the name-display picker shows: the reader's own choice, else the server default. */
export function nameFormatChoice(config: string | null | undefined, locked: string | null | undefined, preference: string | null | undefined): { editable: boolean; value: NameFormat } {
  const valid = (v: string | null | undefined): v is NameFormat => v === 'username' || v === 'nickname_full_name' || v === 'full_name';
  const isLocked = locked === 'true';
  const value = !isLocked && valid(preference) ? preference : valid(config) ? config : 'full_name';
  return { editable: !isLocked, value };
}

// MARK: - Sessions

export interface RawSession {
  id: string;
  create_at?: number;
  last_activity_at?: number;
  expires_at?: number;
  device_id?: string;
  props?: Record<string, string>;
}

export interface SessionRow {
  id: string;
  kind: 'browser' | 'mobile' | 'desktop' | 'other';
  /** "Chrome · Linux", "iPhone", … — what the person recognises the session by. */
  device: string;
  lastActivityAt: number;
  createAt: number;
  current: boolean;
}

/**
 * The webapp's "Sesiones activas" rows. The current session is recognised by its CSRF token (the
 * one value this page and its own session share that the API returns); sorted current first,
 * then most recently active.
 */
export function sessionRows(sessions: readonly RawSession[], currentCsrf: string | null): SessionRow[] {
  // §41.10.1: access tokens are integrations, not sign-ins; `Unknown` (an unparsed User-Agent, every
  // Satori Chat native app) is no name — the push id prefix names the app instead.
  const rows = sessions.filter((s) => s.props?.type !== 'UserAccessToken').map((s): SessionRow => {
    const p = s.props ?? {};
    const prop = (key: string) => {
      const v = (p[key] ?? '').trim();
      return v.replace(/\/.*$/, '').toLowerCase() === 'unknown' ? '' : v;
    };
    const os = prop('os');
    const browser = prop('browser');
    const platform = prop('platform');
    const deviceId = s.device_id ?? '';
    const pushPrefix = deviceId.includes(':') ? deviceId.slice(0, deviceId.indexOf(':')) : '';
    const officialApp = pushPrefix.endsWith('_rn-v2') || browser.startsWith('Mobile App') || browser.startsWith('Desktop App');
    const desktopApp = /desktop/i.test(browser) || /electron/i.test(browser) || p.is_desktop_app === 'true';
    const webPush = pushPrefix === 'android_rn-v4';
    const kind: SessionRow['kind'] =
      pushPrefix === 'android_rn-v3' ? 'mobile'
      : pushPrefix === 'apple_rn-v3' ? 'other' // iPhone, iPad or Mac: the id cannot tell
      : (deviceId && !webPush) || p.isMobile === 'true' || p.is_mobile === 'true' ? 'mobile'
      : desktopApp ? 'desktop'
      : browser ? 'browser'
      : 'other';
    const name = pushPrefix.endsWith('_rn-v3') ? 'Satori Chat' : officialApp ? 'Mattermost' : browser.replace(/\/.*$/, '');
    const system =
      pushPrefix === 'android_rn-v3' || pushPrefix === 'android_rn-v2' ? 'Android'
      : pushPrefix === 'apple_rn-v3' ? 'Apple'
      : pushPrefix === 'apple_rn-v2' ? 'iOS'
      : os || platform;
    const device = [name, system].filter(Boolean).join(' · ');
    return {
      id: s.id,
      kind,
      device,
      lastActivityAt: s.last_activity_at ?? 0,
      createAt: s.create_at ?? 0,
      current: !!currentCsrf && p.csrf === currentCsrf,
    };
  });
  return rows.sort((a, b) => Number(b.current) - Number(a.current) || b.lastActivityAt - a.lastActivityAt);
}

// MARK: - Password

export type PasswordProblem = 'missingCurrent' | 'missingNew' | 'mismatch' | 'same';

/** What stops the form from being sent; the server's own rules (length, symbols) answer after. */
export function passwordProblem(current: string, next: string, confirm: string): PasswordProblem | null {
  if (!current) return 'missingCurrent';
  if (!next) return 'missingNew';
  if (next !== confirm) return 'mismatch';
  if (next === current) return 'same';
  return null;
}

// MARK: - Email notifications

/** `notifications/email_interval` preference values (seconds), in menu order. */
export const emailIntervals = ['30', '900', '3600'] as const;
export type EmailInterval = (typeof emailIntervals)[number];

export function emailIntervalChoice(pref: string | null | undefined): EmailInterval {
  return (emailIntervals as readonly string[]).includes(pref ?? '') ? (pref as EmailInterval) : '30';
}
